import React, { useState, useCallback, useEffect } from 'react';
import * as styles from './SaveFileModal.css.ts';
import { getRendererLogger } from '../utils/logger';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import { TextField } from './common/TextField';

const logger = getRendererLogger();

interface SaveFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (filePath: string) => void | Promise<void>;
  defaultFileName?: string;
  fileExtension?: string;
  fileTypeName?: string;
  title?: string;
}

/**
 * ファイル保存モーダル
 * OSのネイティブダイアログの代わりに使用し、E2Eテストを可能にする
 */
export function SaveFileModal({
  isOpen,
  onClose,
  onSave,
  defaultFileName = 'untitled',
  fileExtension = '.mds',
  fileTypeName = 'MIDAS Project',
  title = 'Save File'
}: SaveFileModalProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [error, setError] = useState<string | null>(null);

  // Web版ではディレクトリ選択は不要（ブラウザのダウンロードフォルダに自動保存）


  // ファイル名のバリデーション
  const validateFileName = useCallback((name: string): boolean => {
    // 空文字チェック
    if (!name.trim()) {
      setError('File name cannot be empty');
      return false;
    }

    // 無効な文字のチェック
    const invalidChars = /[<>:"|?*/\\]/;
    if (invalidChars.test(name)) {
      setError('File name contains invalid characters');
      return false;
    }

    // 長さチェック
    if (name.length > 255) {
      setError('File name is too long');
      return false;
    }

    setError(null);
    return true;
  }, []);

  // ファイル名の変更処理
  const handleFileNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFileName(newName);
    validateFileName(newName);
  }, [validateFileName]);


  // 保存処理
  const handleSave = useCallback(async () => {
    if (!validateFileName(fileName)) {
      return;
    }

    // 拡張子を追加（必要な場合）
    let fullFileName = fileName;
    if (!fileName.endsWith(fileExtension)) {
      fullFileName = fileName + fileExtension;
    }

    // Web版: ファイル名のみ使用
    logger.info('[SaveFileModal] Saving file', {
      fileName: fullFileName
    });

    // onSaveがPromiseを返す場合は待つ
    logger.info('[SaveFileModal] Calling onSave with fileName', { fullFileName });
    const result = onSave(fullFileName);
    logger.info('[SaveFileModal] onSave result type', { isPromise: result && typeof result.then === 'function' });
    if (result && typeof result.then === 'function') {
      logger.info('[SaveFileModal] Waiting for promise');
      await result;
      logger.info('[SaveFileModal] Promise resolved');
    }
    logger.info('[SaveFileModal] Closing modal');
    onClose();
  }, [fileName, fileExtension, validateFileName, onSave, onClose]);

  // キャンセル処理
  const handleCancel = useCallback(() => {
    setFileName(defaultFileName);
    setError(null);
    onClose();
  }, [defaultFileName, onClose]);

  // Enterキーで保存
  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen && !error) {
        handleSave();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEnter);
    }

    return () => {
      document.removeEventListener('keydown', handleEnter);
    };
  }, [isOpen, error, handleSave]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      width="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleCancel}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!!error || !fileName.trim()}
            data-testid="save-button"
          >
            Save
          </Button>
        </>
      }
    >
      <div data-testid="save-file-modal">
        <div className={styles.formGroup}>
          <label htmlFor="fileName">File Name:</label>
          <div className={styles.fileNameInput}>
            <TextField
              id="fileName"
              type="text"
              value={fileName}
              onChange={handleFileNameChange}
              placeholder="Enter file name"
              autoFocus
              error={error || undefined}
              fullWidth
              data-testid="filename-input"
            />
            <span className={styles.extension}>{fileExtension}</span>
          </div>
        </div>

        <div className={styles.fileTypeInfo}>
          <span>File Type: {fileTypeName}</span>
        </div>
      </div>
    </Dialog>
  );
}